import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import {
  resetAppViewportBaseline,
} from '../../lib/mobileViewport';

export type MobileNavItem = {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  badge?: number;
};

type MobileBottomNavProps = {
  items: MobileNavItem[];
  extraItems?: MobileNavItem[];
  showDivider?: boolean;
  className?: string;
};

const SCROLL_EDGE = 4;
const MOBILE_DOCK_MQ = '(max-width: 900px)';

function useMobileDockLayout() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_DOCK_MQ).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_DOCK_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

/** Fallback until dock is measured. */
export const DOCK_CLEARANCE_PX = 76;

function normalizePath(path: string) {
  const trimmed = path.replace(/\/+$/, '');
  return trimmed || '/';
}

function isPathActive(pathname: string, to: string, end?: boolean) {
  const path = normalizePath(pathname);
  const target = normalizePath(to);
  if (end) return path === target;
  return path === target || path.startsWith(`${target}/`);
}

function formatNavBadge(count: number): string {
  if (count > 99) return '99+';
  return String(count);
}

function NavItemLink({ item, pathname }: { item: MobileNavItem; pathname: string }) {
  const active = isPathActive(pathname, item.to, item.end);
  const badge = item.badge && item.badge > 0 ? item.badge : 0;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      role="listitem"
      aria-label={badge > 0 ? `${item.label}, ${badge} unread` : item.label}
      className={() => `dock-nav__item${active ? ' dock-nav__item--active' : ''}`}
      onClick={(e) => {
        (e.currentTarget as HTMLAnchorElement).blur();
      }}
    >
      <span className="dock-nav__icon-slot" aria-hidden>
        <span className="dock-nav__icon">{item.icon}</span>
        {badge > 0 && <span className="dock-nav__badge">{formatNavBadge(badge)}</span>}
      </span>
      <span className="dock-nav__label">{item.label}</span>
    </NavLink>
  );
}

function readScrollEdges(el: HTMLDivElement) {
  const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
  const left = el.scrollLeft;
  return {
    maxScroll,
    hasOverflow: maxScroll > SCROLL_EDGE,
    atStart: left <= SCROLL_EDGE,
    atEnd: left >= maxScroll - SCROLL_EDGE,
  };
}

/** Fixed bottom dock on mobile (portaled to body); in-flow on desktop. */
export function MobileBottomNav({
  items,
  extraItems = [],
  showDivider = false,
  className = '',
}: MobileBottomNavProps) {
  const { pathname } = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [canScroll, setCanScroll] = useState(false);
  const [showPrevArrow, setShowPrevArrow] = useState(false);
  const [showNextArrow, setShowNextArrow] = useState(false);

  const updateScrollArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { hasOverflow, atStart, atEnd } = readScrollEdges(el);
    setCanScroll(hasOverflow);
    setShowPrevArrow(hasOverflow && !atStart);
    setShowNextArrow(hasOverflow && !atEnd);
  }, []);

  const isMobileDock = useMobileDockLayout();

  useEffect(() => {
    document.documentElement.classList.add('has-dock');
    document.documentElement.classList.remove('has-auth-shell', 'has-portal-chooser');
    document.documentElement.style.setProperty('--dock-clearance', `${DOCK_CLEARANCE_PX}px`);
    document.documentElement.style.removeProperty('--app-height');
    resetAppViewportBaseline();
    // One cleanup only — no multi-second recover pulse (that flashed full→safe-area).
    const nav = document.querySelector('.dock-nav') as HTMLElement | null;
    if (nav) {
      nav.style.removeProperty('bottom');
      nav.style.removeProperty('left');
      nav.style.removeProperty('right');
      nav.style.removeProperty('width');
      nav.style.removeProperty('transform');
    }

    const syncClearance = () => {
      const isMobileDockMq = window.matchMedia(MOBILE_DOCK_MQ).matches;
      const dockEl = document.querySelector('.dock-nav') as HTMLElement | null;
      const shell = dockEl?.querySelector('.dock-nav__shell') as HTMLElement | null;

      // While the software keyboard is open, visualViewport shrinks — skip so we
      // don't bake a huge clearance / leave the dock stranded after dismiss.
      const vv = window.visualViewport;
      const layoutH = Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0);
      const keyboardOpen =
        Boolean(vv) &&
        Math.abs((vv?.scale ?? 1) - 1) < 0.02 &&
        layoutH - (vv?.height ?? layoutH) > 120;
      if (keyboardOpen) return;

      if (shell) {
        const top = shell.getBoundingClientRect().top;
        const clearance = Math.ceil(layoutH - top);
        if (clearance > 0 && clearance < layoutH * 0.55) {
          document.documentElement.style.setProperty('--dock-clearance', `${clearance}px`);
        }
      } else if (dockEl) {
        const height = Math.ceil(dockEl.getBoundingClientRect().height);
        if (height > 0) {
          document.documentElement.style.setProperty('--dock-clearance', `${height}px`);
        }
      }

      if (isMobileDockMq) {
        const topbar = document.querySelector('.app-shell--dock .topbar') as HTMLElement | null;
        if (topbar) {
          const headerHeight = Math.ceil(topbar.getBoundingClientRect().height);
          if (headerHeight > 0) {
            document.documentElement.style.setProperty(
              '--mobile-header-clearance',
              `${headerHeight}px`,
            );
          }
        }
      } else {
        document.documentElement.style.removeProperty('--mobile-header-clearance');
      }
    };

    syncClearance();
    const shell = nav?.querySelector('.dock-nav__shell');
    const ro = nav ? new ResizeObserver(syncClearance) : null;
    if (nav && ro) ro.observe(nav);
    if (shell && ro) ro.observe(shell);
    window.addEventListener('resize', syncClearance);
    window.addEventListener('orientationchange', syncClearance);
    window.addEventListener('bth:viewport-recover', syncClearance);
    window.visualViewport?.addEventListener('resize', syncClearance);
    window.requestAnimationFrame(syncClearance);

    return () => {
      document.documentElement.classList.remove('has-dock');
      document.documentElement.style.removeProperty('--dock-clearance');
      document.documentElement.style.removeProperty('--mobile-header-clearance');
      ro?.disconnect();
      window.removeEventListener('resize', syncClearance);
      window.removeEventListener('orientationchange', syncClearance);
      window.removeEventListener('bth:viewport-recover', syncClearance);
      window.visualViewport?.removeEventListener('resize', syncClearance);
    };
  }, []);

  useEffect(() => {
    updateScrollArrows();
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      updateScrollArrows();
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
      scrollEndTimer.current = setTimeout(updateScrollArrows, 120);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('scrollend', updateScrollArrows);
    const ro = new ResizeObserver(updateScrollArrows);
    ro.observe(el);
    window.addEventListener('resize', updateScrollArrows);
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('scrollend', updateScrollArrows);
      ro.disconnect();
      window.removeEventListener('resize', updateScrollArrows);
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
    };
  }, [items.length, extraItems.length, updateScrollArrows]);

  const scrollByPage = (direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.round(el.clientWidth * 0.8);
    const { maxScroll } = readScrollEdges(el);
    const target = Math.max(0, Math.min(maxScroll, el.scrollLeft + direction * step));
    el.scrollTo({ left: target, behavior: 'smooth' });
    window.setTimeout(updateScrollArrows, 350);
  };

  const dock = (
    <nav
      className={`dock-nav${className ? ` ${className}` : ''}`}
      aria-label="Main navigation"
    >
      <div className="dock-nav__inner">
        <div className="dock-nav__shell">
          <div className="dock-nav__glass" aria-hidden />
          <div className={`dock-nav__viewport${canScroll ? ' dock-nav__viewport--scroll' : ''}`}>
            {canScroll ? (
              <button
                type="button"
                className={`dock-nav__arrow dock-nav__arrow--left${showPrevArrow ? '' : ' dock-nav__arrow--hidden'}`}
                aria-label="Scroll navigation left"
                aria-hidden={!showPrevArrow}
                tabIndex={showPrevArrow ? 0 : -1}
                disabled={!showPrevArrow}
                onClick={() => scrollByPage(-1)}
              >
                <span className="dock-nav__arrow-icon" aria-hidden />
              </button>
            ) : null}
            <div
              ref={scrollRef}
              className="dock-nav__track"
              role="list"
              aria-label="App sections"
            >
              {items.map((item) => (
                <NavItemLink key={item.to} item={item} pathname={pathname} />
              ))}
              {showDivider && extraItems.length > 0 ? (
                <div className="dock-nav__divider" role="separator" aria-orientation="vertical" />
              ) : null}
              {extraItems.map((item) => (
                <NavItemLink key={item.to} item={item} pathname={pathname} />
              ))}
            </div>
            {canScroll ? (
              <button
                type="button"
                className={`dock-nav__arrow dock-nav__arrow--right${showNextArrow ? '' : ' dock-nav__arrow--hidden'}`}
                aria-label="Scroll navigation right"
                aria-hidden={!showNextArrow}
                tabIndex={showNextArrow ? 0 : -1}
                disabled={!showNextArrow}
                onClick={() => scrollByPage(1)}
              >
                <span className="dock-nav__arrow-icon" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </nav>
  );

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  return isMobileDock && portalTarget ? createPortal(dock, portalTarget) : dock;
}
